import { createColumnHelper } from '@tanstack/react-table';
import { UnicodeEntity } from '@mews/enums';
import { UserGroup } from '@mews-commander/data-access';
import { LocalizedEnum, LocalizedText, useDialog } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { DropDownButton, DropdownItem, IconName, Stack, Tag } from '@optimus-web/core';
import { GroupSettings } from '../GroupSettings';
import { GroupAvatar } from './GroupAvatar';
import { AccessPolicyCell } from './AccessPolicy';
import { AdvanceOptionsCell } from './AdvancedOptions';

const columnHelper = createColumnHelper<UserGroup>();

export const useColumns = ({ onOpenDetails }: { onOpenDetails: (group: UserGroup) => void }) => [
    columnHelper.accessor('name', {
        id: 'name',
        header: () => <LocalizedText textKey={TextKey.GroupName} />,
        cell: (props) => <GroupAvatar onClick={() => onOpenDetails(props.row.original)} groupName={props.getValue()} />,
        enableSorting: true,
        meta: {
            width: 366,
        },
    }),
    columnHelper.accessor('enterprises', {
        id: 'enterprises',
        header: () => <LocalizedText textKey={TextKey.Properties} />,
        cell: (props) => {
            const tagsToDisplay = 2;
            const values = props.getValue() ?? [];
            return values.length === 0 ? (
                UnicodeEntity.Dash
            ) : (
                <Stack spacing="50" as="span" onClick={() => onOpenDetails(props.row.original)}>
                    {values.slice(0, tagsToDisplay).map((p) => (
                        <Tag key={p.id} variant="basicSubtle">
                            {p.name}
                        </Tag>
                    ))}
                    {values.length > tagsToDisplay && (
                        <Tag key="more" variant="basicSubtle">
                            +{values.length - tagsToDisplay}
                        </Tag>
                    )}
                </Stack>
            );
        },
    }),
    columnHelper.accessor('accessPolicy', {
        header: () => <LocalizedText textKey={TextKey.Roles} />,
        cell: (props) => <AccessPolicyCell accessPolicy={props.getValue()} />,
    }),
    columnHelper.accessor('accessPolicy', {
        id: 'advancedOptions',
        header: () => <LocalizedText textKey={TextKey.AdvancedSettings} />,
        cell: (props) => <AdvanceOptionsCell options={props.getValue()?.enterpriseProfileOptions} />,
    }),
    columnHelper.accessor('assignedLearningPath', {
        header: () => <LocalizedText textKey={TextKey.LearningPath} />,
        cell: (props) => {
            const learningPath = props.getValue();

            if (learningPath) {
                return <LocalizedEnum prefix="LearningPath" value={learningPath} />;
            }
            return UnicodeEntity.Dash;
        },
    }),
    columnHelper.accessor('cashierExternalIdentifiers', {
        header: () => <LocalizedText textKey={TextKey.CashierPlural} />,
        cell: (props) => props.getValue()?.join(', ') || UnicodeEntity.Dash,
    }),
    columnHelper.accessor('userCount', {
        header: () => <LocalizedText textKey={TextKey.UserPlural} />,
        cell: (props) => props.getValue(),
        enableSorting: true,
        meta: {
            width: 120,
        },
    }),
    columnHelper.display({
        id: 'actions',
        meta: { width: 100, align: 'end' },
        cell: (props) => <GroupActions group={props.row.original} onOpenDetails={onOpenDetails} />,
    }),
];

const GroupActions = ({ group, onOpenDetails }: { group: UserGroup; onOpenDetails: (group: UserGroup) => void }) => {
    const [editGroupDialogProps, toggleEditGroupDialog] = useDialog();

    return (
        <>
            <DropDownButton icon={IconName.More} size="small" variant="ghost">
                <DropdownItem
                    label={<LocalizedText textKey={TextKey.ViewDetails} />}
                    onClick={() => onOpenDetails(group)}
                />
                <DropdownItem label={<LocalizedText textKey={TextKey.EditDetails} />} onClick={toggleEditGroupDialog} />
            </DropDownButton>

            <GroupSettings group={group} {...editGroupDialogProps} />
        </>
    );
};
